// AP-077/AP-080 · Portal do Cliente Premium — Redesign UI (Fase 1)
//   • Header moderno: logotipo, saudação "👋 Olá, {nome}!" visível em todas as
//     telas, sino de notificações com badge (não lidas), avatar clicável.
//   • Sidebar (desktop): preservada em estrutura, com refinamentos visuais.
//   • Bottom Navigation (mobile): 5 ícones — Início, Processos, Documentos,
//     Notificações, Conta.
//   • Logout: mantém AlertDialog "Sair do Portal?".
//   • Zero mudança em auth, rotas, DB, RBAC, APIs ou regras de negócio.
import React, { useState } from "react";
import { NavLink, Outlet, Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, FileText, Receipt, CalendarClock, Bell,
  User as UserIcon, LogOut, Archive, HelpCircle, ChevronDown, MoreHorizontal, Bot,
  Smartphone, LifeBuoy,
} from "lucide-react";
import { usePortalAuth } from "../../context/PortalAuthContext";
import { usePortalHeartbeat } from "../../hooks/usePortalHeartbeat";
import PortalNotificationBell from "./PortalNotificationBell";
import PortalLanguageSwitcher from "./PortalLanguageSwitcher";
import PortalInstallGuide, { useInstallGuide } from "./PortalInstallGuide";
import { usePortalI18n } from "./PortalI18nContext";
import { PortalSettingsProvider, usePortalSettings } from "./PortalSettingsContext";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

// ---- Sidebar items (labels via i18n) ----
const sidebarItemsBase = [
  { to: "/portal",               end: true,  key: "nav.dashboard",     icon: LayoutDashboard, testid: "portal-nav-dashboard" },
  { to: "/portal/processes",     end: false, key: "nav.processes",     icon: FolderKanban,    testid: "portal-nav-processes" },
  { to: "/portal/documents",     end: false, key: "nav.documents",     icon: FileText,        testid: "portal-nav-documents" },
  { to: "/portal/central-docs",  end: false, key: "nav.centralDocs",   icon: Archive,         testid: "portal-nav-central-docs" },
  { to: "/portal/receipts",      end: false, key: "nav.receipts",      icon: Receipt,         testid: "portal-nav-receipts" },
  { to: "/portal/appointments",  end: false, key: "nav.appointments",  icon: CalendarClock,   testid: "portal-nav-appointments" },
  { to: "/portal/assistente",    end: false, key: "nav.assistant",     icon: Bot,             testid: "portal-nav-assistant" },
  { to: "/portal/tickets",       end: false, key: "nav.tickets",       icon: LifeBuoy,        testid: "portal-nav-tickets" },
  { to: "/portal/notifications", end: false, key: "nav.notifications", icon: Bell,            testid: "portal-nav-notifications" },
];

// ---- Bottom Navigation (mobile) — 4 itens principais ----
// AP-120 · Ajuda migrou para /portal/assistente (Assistente IA JMS).
// WhatsApp permanece APENAS no PortalLogin para clientes ainda sem acesso.
const openHelp = () => { window.location.href = "/portal/assistente"; };

// ---- Avatar (foto do cliente ou iniciais) ----
function Avatar({ url, name, size = 9 }) {
  const initials =
    (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "C";
  const cls = `h-${size} w-${size} rounded-full grid place-items-center text-sm font-bold overflow-hidden bg-primary/10 border border-primary/30 text-primary`;
  if (url) return <img src={url} alt={name || ""} className={`${cls} object-cover`} />;
  return <div className={cls}>{initials}</div>;
}

// ---- Menu do Perfil (dropdown desktop + sheet mobile via bottom nav) ----
function ProfileMenuItems({ onLogout, onNavigate, onInstallGuide }) {
  const { t } = usePortalI18n();
  const linkClass = "flex items-center gap-2.5 text-sm cursor-pointer transition-colors";
  const rows = [
    { to: "/portal",               label: t("menu.myProfile"),  icon: UserIcon,        testid: "profile-menu-account" },
    { to: "/portal/processes",     label: t("nav.processes"),   icon: FolderKanban,    testid: "profile-menu-processes" },
    { to: "/portal/documents",     label: t("nav.documents"),   icon: FileText,        testid: "profile-menu-documents" },
    { to: "/portal/receipts",      label: t("nav.receipts"),    icon: Receipt,         testid: "profile-menu-receipts" },
    { to: "/portal/appointments",  label: t("nav.appointments"), icon: CalendarClock,  testid: "profile-menu-appointments" },
    { to: "/portal/notifications", label: t("nav.notifications"), icon: Bell,          testid: "profile-menu-notifications" },
  ];
  return (
    <>
      {rows.map(({ to, label, icon: Icon, testid }) => (
        <DropdownMenuItem key={to} data-testid={testid} onSelect={() => onNavigate(to)} className={linkClass}>
          <Icon className="h-4 w-4 text-muted-foreground" />
          <span>{label}</span>
        </DropdownMenuItem>
      ))}
      {/* AP-162 · Guia rápido "adicionar à tela inicial" */}
      <DropdownMenuItem data-testid="profile-menu-install" onSelect={() => onInstallGuide?.()} className={linkClass}>
        <Smartphone className="h-4 w-4 text-primary" />
        <span>{t("menu.install")}</span>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem data-testid="profile-menu-help" onSelect={openHelp} className={linkClass}>
        <HelpCircle className="h-4 w-4 text-muted-foreground" />
        <span>{t("menu.help")}</span>
      </DropdownMenuItem>
      <DropdownMenuItem
        data-testid="profile-menu-logout"
        onSelect={(e) => { e.preventDefault?.(); onLogout(); }}
        className={`${linkClass} text-destructive focus:text-destructive focus:bg-destructive/10`}
      >
        <LogOut className="h-4 w-4" />
        <span>{t("menu.logout")}</span>
      </DropdownMenuItem>
    </>
  );
}

// AP-103 · Marca dinâmica — quando o Master define `portal_logo_url` no painel
// de configurações, usamos essa imagem no lugar do "J" padrão. Aplica-se ao
// header mobile e à sidebar desktop.
function BrandLogo({ variant = "sidebar" }) {
  const { settings } = usePortalSettings();
  const url = (settings?.portal_logo_url || "").trim();
  const sizeCls = variant === "sidebar" ? "h-10 w-10" : "h-9 w-9";
  const textCls = variant === "sidebar" ? "text-lg" : "text-base";
  const ringCls = variant === "sidebar" ? "ring-white/10" : "ring-primary/30";
  if (url) {
    return (
      <img
        src={url}
        alt="Logo"
        data-testid={`portal-brand-logo-${variant}`}
        className={`${sizeCls} rounded-full object-cover shadow-md ring-1 ${ringCls} bg-white`}
      />
    );
  }
  return (
    <div
      data-testid={`portal-brand-logo-${variant}`}
      className={`${sizeCls} rounded-full bg-gradient-to-br from-primary to-primary/70 grid place-items-center shadow-md ring-1 ${ringCls}`}
    >
      <span className={`font-display font-black text-white ${textCls} leading-none`}>J</span>
    </div>
  );
}

export default function PortalLayout() {
  const { client, loading, logout } = usePortalAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const [busyLogout, setBusyLogout] = useState(false);
  const { t } = usePortalI18n();
  // AP-076 F3.1 · mantém o cliente "Online" no Centro de Monitoramento.
  usePortalHeartbeat(client?.id);

  if (loading) {
    return (
      <div data-testid="portal-loading" className="min-h-screen grid place-items-center text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }
  if (!client) {
    return <Navigate to="/portal/login" replace state={{ from: location.pathname }} />;
  }

  // AP-103 FINAL · O Provider é montado somente após a autenticação para que
  // `GET /portal/settings` (com token de portal_client) responda 200.
  return (
    <PortalSettingsProvider>
      <PortalLayoutBody
        client={client}
        logout={logout}
        location={location}
        navigate={navigate}
        confirmLogoutOpen={confirmLogoutOpen}
        setConfirmLogoutOpen={setConfirmLogoutOpen}
        busyLogout={busyLogout}
        setBusyLogout={setBusyLogout}
      />
    </PortalSettingsProvider>
  );
}

function PortalLayoutBody({ client, logout, location, navigate, confirmLogoutOpen, setConfirmLogoutOpen, busyLogout, setBusyLogout }) {
  const { t } = usePortalI18n();
  const { settings } = usePortalSettings();
  // AP-162 · Guia "adicionar à tela inicial" (sem PWA).
  const installGuide = useInstallGuide();

  const firstName = (client.full_name || "Cliente").trim().split(/\s+/)[0];
  const avatarUrl = client.photo_data_url || client.photo_url || client.avatar_url || "";
  // AP-103 FINAL · Filtra a nav com base nas flags do tenant (portal_show_*).
  const shownItems = sidebarItemsBase.filter((s) => {
    if (s.to === "/portal/documents"     && settings.portal_show_documents === false)     return false;
    if (s.to === "/portal/central-docs"  && settings.portal_show_documents === false)     return false;
    if (s.to === "/portal/receipts"      && settings.portal_show_receipts === false)      return false;
    if (s.to === "/portal/appointments"  && settings.portal_show_appointments === false)  return false;
    if (s.to === "/portal/notifications" && settings.portal_show_notifications === false) return false;
    if (s.to === "/portal/processes"     && settings.portal_show_processes === false)     return false;
    return true;
  });
  const sidebarItems = shownItems.map((s) => ({ ...s, label: t(s.key) }));

  const askLogout = () => setConfirmLogoutOpen(true);
  const confirmLogout = async () => {
    if (busyLogout) return;
    setBusyLogout(true);
    try { await logout(); } finally {
      setBusyLogout(false);
      setConfirmLogoutOpen(false);
      navigate("/portal/login", { replace: true });
    }
  };

  // Bottom nav (mobile) — 3 rotas + 1 dropdown "Minha Conta".
  const bottomItems = [
    { to: "/portal",               end: true,  label: t("bnav.home"),      icon: LayoutDashboard, testid: "portal-bottom-dashboard" },
    { to: "/portal/processes",     end: false, label: t("bnav.processes"), icon: FolderKanban,    testid: "portal-bottom-processes" },
    { to: "/portal/notifications", end: false, label: t("bnav.notifs"),    icon: Bell,            testid: "portal-bottom-notifications" },
  ];

  return (
    <div className="portal-blue-theme min-h-screen bg-background flex" data-testid="portal-layout">
      {/* Sidebar (desktop) — inalterada */}
      {/* AP-081 · Sidebar escura Blue Edition */}
      <aside
        data-testid="portal-sidebar"
        className="portal-sidebar-dark hidden md:flex md:flex-col md:w-64 shrink-0 border-r sticky top-0 h-screen"
      >
        <div className="px-6 py-6 border-b" style={{ borderColor: "hsl(222 47% 18%)" }}>
          <div className="flex items-center gap-3">
            {/* AP-103 · Logo dinâmico do tenant (fallback = "J" gradient) */}
            <BrandLogo variant="sidebar" />
            <div>
              <p className="font-display font-bold text-base tracking-tight text-white">
                {t("brand.title")}
              </p>
              <p className="portal-brand-sub text-[11px] uppercase tracking-widest">{t("brand.subtitle")}</p>
              <p className="portal-brand-sub text-[10px] italic mt-0.5">{t("brand.tagline")}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {sidebarItems.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `nav-item flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium border-l-2 transition-all duration-150 ${
                  isActive ? "" : "hover:translate-x-0.5"
                }`
              }
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="portal-sidebar-footer px-4 py-4 border-t">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate text-white" data-testid="portal-client-name">{firstName}</p>
              <p className="portal-brand-sub text-xs truncate">{t("footer.clientArea")}</p>
            </div>
            <button
              data-testid="portal-logout-button"
              onClick={askLogout}
              className="p-2 rounded-md text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
              title={t("footer.exitTitle")}
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Área principal */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — AP-080 · Header premium com logo mobile, saudação, sino e avatar */}
        <header
          data-testid="portal-topbar"
          className="h-16 bg-white/95 backdrop-blur-sm border-b border-border flex items-center justify-between gap-3 px-4 md:px-8 sticky top-0 z-30"
        >
          <div className="flex items-center gap-3 min-w-0">
            {/* AP-103 · Logo dinâmico do tenant no header mobile */}
            <div className="md:hidden shrink-0">
              <BrandLogo variant="topbar" />
            </div>
            <div className="min-w-0 leading-tight">
              <p className="text-sm md:text-base font-semibold text-foreground truncate" data-testid="portal-topbar-hello">
                {t("header.hi")}, {firstName} <span aria-hidden="true">👋</span>
              </p>
              <p className="hidden md:block text-[11px] text-muted-foreground truncate max-w-[320px]">
                {t("header.welcome")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            {/* AP-082 · Seletor de idioma (Kreyòl/PT/EN/FR/ES) */}
            <PortalLanguageSwitcher />
            {/* Sino de notificações com badge — sempre visível */}
            <PortalNotificationBell />

            {/* Avatar + Dropdown (desktop + mobile) */}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="portal-profile-trigger"
                className="flex items-center gap-2 rounded-full pl-1.5 pr-2 md:pr-3 py-1 hover:bg-muted/60 transition-all duration-150 active:scale-95"
                aria-label="Abrir menu do perfil"
              >
                <Avatar url={avatarUrl} name={client.full_name} />
                <ChevronDown className="h-3.5 w-3.5 text-muted-foreground hidden md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-64 animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
              data-testid="portal-profile-menu"
            >
              <DropdownMenuLabel className="flex items-center gap-3 py-3">
                <Avatar url={avatarUrl} name={client.full_name} size={10} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{client.full_name || firstName}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{client.email || t("brand.title")}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ProfileMenuItems
                onNavigate={(to) => navigate(to)}
                onLogout={askLogout}
                onInstallGuide={installGuide.show}
              />
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Conteúdo · com padding extra no mobile para bottom-nav */}
        <main
          data-testid="portal-main"
          className="flex-1 p-4 md:p-8 pb-24 md:pb-8 max-w-6xl w-full mx-auto animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
          key={location.pathname /* re-anima ao trocar de rota */}
        >
          <Outlet />
        </main>

        <footer className="hidden md:block border-t border-border bg-white/60 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between text-[11px] text-muted-foreground">
            <div className="flex items-center gap-2">
              <div className="h-5 w-5 rounded-full bg-gradient-to-br from-primary to-primary/70 grid place-items-center">
                <span className="font-display font-black text-white text-[10px] leading-none">J</span>
              </div>
              <span>{t("footer.copy")} · {t("footer.cnpj")}</span>
            </div>
            <span className="tabular-nums">{t("footer.version")}</span>
          </div>
        </footer>
      </div>

      {/* Bottom Navigation (mobile) — fixed */}
      <nav
        data-testid="portal-bottom-nav"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
      >
        <div className="max-w-lg mx-auto grid grid-cols-5">
          {bottomItems.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-all duration-150 active:scale-90 ${
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`inline-flex h-6 w-10 items-center justify-center rounded-full transition-colors ${isActive ? "bg-primary/15" : ""}`}>
                    <Icon className="h-4 w-4" strokeWidth={2} />
                  </span>
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          ))}
          {/* Botão "Minha Conta" → abre o mesmo dropdown do avatar (Radix menu). */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="portal-bottom-account"
                className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium text-muted-foreground hover:text-foreground transition-all duration-150 active:scale-90"
              >
                <span className="inline-flex h-6 w-10 items-center justify-center rounded-full">
                  <MoreHorizontal className="h-4 w-4" strokeWidth={2} />
                </span>
                <span>{t("bnav.account")}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end" side="top" sideOffset={12}
              className="w-64 animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2 duration-200"
              data-testid="portal-bottom-menu"
            >
              <DropdownMenuLabel className="flex items-center gap-3 py-3">
                <Avatar url={avatarUrl} name={client.full_name} size={10} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{client.full_name || firstName}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{client.email || t("brand.title")}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <ProfileMenuItems onNavigate={(to) => navigate(to)} onLogout={askLogout} onInstallGuide={installGuide.show} />
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Confirmação de logout */}
      <AlertDialog open={confirmLogoutOpen} onOpenChange={setConfirmLogoutOpen}>
        <AlertDialogContent data-testid="portal-logout-dialog" className="animate-in fade-in-0 zoom-in-95 duration-200">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("logout.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("logout.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="portal-logout-cancel" disabled={busyLogout}>{t("logout.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="portal-logout-confirm"
              onClick={confirmLogout}
              disabled={busyLogout}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busyLogout ? `${t("logout.confirm")}…` : t("logout.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AP-162 · Guia "adicionar à tela inicial" */}
      <PortalInstallGuide
        open={installGuide.open}
        onClose={installGuide.close}
        onDismiss={installGuide.dismissForever}
      />
    </div>
  );
}
